/* Picking somebody out of the contact book — STORY-016.
 *
 * The markup and class names are exactly `design/components/person-picker`'s.
 * `currency-select` is the same ARIA combobox on the same CSS; the differences
 * are that this one searches a list held on the server and that "not in the
 * book yet" is an ordinary outcome rather than an error.
 *
 * `design.md` prefers a real `<select>` and permits a custom control only where
 * the behaviour requires one. It does here: a contact book runs to hundreds of
 * names and is searched rather than scanned, and adding somebody new mid-task
 * is the common case rather than the exception — being sent to another screen
 * loses the guest list you were building.
 *
 * Three rules that break quietly if they are not kept:
 *
 *  1. **Keyboard focus stays in the input.** The active option is pointed at
 *     with `aria-activedescendant`, never with `.focus()`. Moving real focus
 *     into the list breaks typing, which is the whole interaction.
 *  2. **Creating is a `<button>`, not an option in the list.** "Add Jo as a new
 *     person" is a command; announcing it as a choosable value invites picking
 *     it by accident while arrowing past.
 *  3. **Nothing is active until an arrow key says so.** Opening with the first
 *     match pre-selected means Enter invites somebody nobody chose — and on a
 *     guest list that is a real person getting a real invitation.
 *
 * Every string arrives as a prop, as `TextField`'s and `CurrencySelect`'s do:
 * the shared UI layer holds no catalogue keys.
 */

import type { KeyboardEvent, ReactNode } from 'react';
import { useId, useState } from 'react';

/** No option is active. Opening the list does not choose anybody. */
const NONE_ACTIVE = -1;

export interface PickablePerson {
  readonly id: string;
  readonly displayName: string;
  /** A phone or an email, to tell two people with one name apart. */
  readonly detail?: string | undefined;
}

export interface PersonPickerProps {
  readonly label: string;
  /** Shown and announced when the query matches nobody. */
  readonly emptyLabel: string;
  /** Announced as the list narrows. Takes the match count. */
  readonly resultsLabel: (count: number) => string;
  /** The create command's label. Takes what has been typed. */
  readonly createLabel: (query: string) => string;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  /** The matches, already searched by whoever owns the query. */
  readonly people: readonly PickablePerson[];
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly onPick: (personId: string) => void;
  /** Absent means creating is not offered — the contact cap, for instance. */
  readonly onCreate?: ((displayName: string) => void) | undefined;
  readonly busy?: boolean;
}

export function PersonPicker({
  label,
  emptyLabel,
  resultsLabel,
  createLabel,
  hint,
  error,
  people,
  query,
  onQueryChange,
  onPick,
  onCreate,
  busy,
}: PersonPickerProps) {
  const id = useId();
  const listId = `${id}-list`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const statusId = `${id}-status`;
  const optionId = (index: number) => `${id}-opt-${index}`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(NONE_ACTIVE);

  const activeOption = activeIndex >= 0 ? people[activeIndex] : undefined;
  const typed = query.trim();

  const close = () => {
    setOpen(false);
    setActiveIndex(NONE_ACTIVE);
  };

  const pick = (person: PickablePerson) => {
    onPick(person.id);
    /* The query is cleared rather than filled with the name: the next thing
     * somebody does on a guest list is add the next guest, and leaving the last
     * one in the box means they delete it first, every time. */
    onQueryChange('');
    close();
  };

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (people.length === 0) return;
      setOpen(true);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        /* From nowhere, the ends — not arithmetic on -1. `(-1 - 1 + n) % n` is
         * `n - 2`, so the first ArrowUp landed on the second-to-last option
         * rather than the last. `currency-select` handles it explicitly for
         * the same reason. */
        if (current === NONE_ACTIVE) return step === 1 ? 0 : people.length - 1;
        return (current + step + people.length) % people.length;
      });
      return;
    }

    if (event.key === 'Enter' && activeOption) {
      /* Only when something is actually active. Otherwise Enter belongs to the
       * form, and swallowing it would break submitting from the keyboard. */
      event.preventDefault();
      pick(activeOption);
      return;
    }

    if (event.key === 'Escape') {
      /* Abandons the list; it does not clear the query. Losing what somebody
       * typed because they dismissed a dropdown is a small betrayal. */
      close();
    }
  }

  const describedBy = [hint ? hintId : null, error ? errorId : null, open ? statusId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="picker">
      <div className="field">
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
        <input
          id={id}
          className="field__control"
          type="text"
          role="combobox"
          autoComplete="off"
          disabled={busy}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          {...(activeOption ? { 'aria-activedescendant': optionId(activeIndex) } : {})}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          value={query}
          onChange={(changed) => {
            onQueryChange(changed.target.value);
            setOpen(true);
            /* A new query means the old position means nothing. */
            setActiveIndex(NONE_ACTIVE);
          }}
          onFocus={() => setOpen(true)}
          /* Closing on blur rather than a document click: the list has no
           * focusable children, so leaving the input is the only way out. */
          onBlur={() => close()}
          onKeyDown={onKeyDown}
        />
        {hint ? (
          <p className="field__hint" id={hintId}>
            {hint}
          </p>
        ) : null}
        {error ? (
          <p className="field__error" id={errorId} role="alert">
            {error}
          </p>
        ) : null}
      </div>

      {open ? (
        <ul
          className="picker__list"
          id={listId}
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: the ARIA combobox pattern puts listbox on the popup and option on its children, with focus staying in the input. Biome reads the element in isolation; a div of divs would lose the semantics a screen reader uses to announce "3 of 8".
          role="listbox"
          aria-label={label}
        >
          {people.map((person, index) => (
            // biome-ignore lint/a11y/useFocusableInteractive: an option here must NOT be focusable. Focus stays in the input so typing keeps working while arrowing, and aria-activedescendant moves the screen reader's cursor instead.
            <li
              className="picker__option"
              id={optionId(index)}
              key={person.id}
              // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: an option inside a listbox is the pattern, not a deviation from it — see the ul above.
              role="option"
              aria-selected={index === activeIndex}
              /* A data attribute rather than a modifier class, which is what
                 the mockup uses — keeping the markup identical is what lets
                 the two be diffed. */
              {...(index === activeIndex ? { 'data-active': 'true' } : {})}
              /* `onMouseDown` rather than `onClick`: blur fires first and
                 would close the list before a click ever landed. */
              onMouseDown={(event) => {
                event.preventDefault();
                pick(person);
              }}
            >
              <span className="picker__main">
                <span className="picker__name">{person.displayName}</span>
                {person.detail ? <span className="picker__note">{person.detail}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* The count, and the empty state, in the same live region — announced as
          the list narrows so a screen-reader user hears how many matches there
          are without arrowing through them to find out. */}
      <p className="picker__status" id={statusId} role="status">
        {open ? (people.length === 0 ? emptyLabel : resultsLabel(people.length)) : ''}
      </p>

      {/* A command, never an option in the list — see the header. Offered only
          when something has been typed that matches nobody exactly. */}
      {onCreate && typed !== '' && !people.some((person) => person.displayName === typed) ? (
        <button
          className="btn btn--ghost"
          type="button"
          disabled={busy}
          onMouseDown={(event) => {
            event.preventDefault();
            onCreate(typed);
            onQueryChange('');
            close();
          }}
        >
          {createLabel(typed)}
        </button>
      ) : null}
    </div>
  );
}
